import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Clock, CheckCircle, Truck, Phone, AlertCircle, Zap, RefreshCw } from 'lucide-react';

interface Technician {
  id: string;
  name: string;
  phone: string;
  avatar: string;
  status: 'on-site' | 'en-route' | 'idle' | 'off-duty';
  currentJob: string;
  location: string;
  lat: number;
  lng: number;
  eta?: string;
  completedToday: number;
  batteryLevel: number;
}

interface Job {
  id: string;
  techId: string;
  customer: string;
  address: string;
  service: string;
  status: 'in-progress' | 'completed' | 'en-route' | 'scheduled';
  time: string;
  duration: string;
}

const technicianData: Technician[] = [
  { id: 'T1', name: 'Kamal Perera', phone: '+94711234567', avatar: 'KP', status: 'on-site', currentJob: 'CCTV Install — AIA Insurance', location: 'Colombo 3, Galle Road', lat: 6.8731, lng: 79.8619, eta: 'On-site', completedToday: 1, batteryLevel: 78 },
  { id: 'T2', name: 'Nimal Silva', phone: '+94729876543', avatar: 'NS', status: 'en-route', currentJob: 'Network Setup — Hotel Reef', location: 'Negombo, A3 Highway', lat: 7.1834, lng: 79.8625, eta: '22 min', completedToday: 2, batteryLevel: 45 },
  { id: 'T3', name: 'Suresh Kumar', phone: '+94715678901', avatar: 'SK', status: 'idle', currentJob: 'Alarm Maintenance — Next 2PM', location: 'Kandy, Peradeniya Rd', lat: 7.2906, lng: 80.6337, eta: '—', completedToday: 3, batteryLevel: 92 },
  { id: 'T4', name: 'Dilshan Fernando', phone: '+94762345678', avatar: 'DF', status: 'off-duty', currentJob: 'Off duty', location: 'Colombo 10 (Home)', lat: 6.9271, lng: 79.8612, eta: '—', completedToday: 4, batteryLevel: 100 },
];

const jobData: Job[] = [
  { id: 'J001', techId: 'T1', customer: 'AIA Insurance', address: '42 Galle Rd, Colombo 3', service: 'CCTV Installation (12 cameras)', status: 'in-progress', time: '9:00 AM', duration: '4 hrs' },
  { id: 'J002', techId: 'T2', customer: 'Hotel Reef', address: 'Negombo Beach Road', service: 'Network Setup + WiFi', status: 'en-route', time: '11:00 AM', duration: '5 hrs' },
  { id: 'J003', techId: 'T3', customer: 'Lanka Hospitals', address: 'Kandy Road', service: 'Alarm System Maintenance', status: 'scheduled', time: '2:00 PM', duration: '2 hrs' },
  { id: 'J004', techId: 'T1', customer: 'MAS Holdings', address: 'Dehiwala Industrial', service: 'Follow-up Service', status: 'scheduled', time: '4:00 PM', duration: '1 hr' },
  { id: 'J005', techId: 'T2', customer: 'Dialog Axiata HQ', address: 'Maradana, Colombo', service: 'Starlink Installation', status: 'completed', time: '8:00 AM', duration: '3 hrs' },
  { id: 'J006', techId: 'T3', customer: 'Sampath Bank', address: 'Kandy City Centre', service: 'CCTV Maintenance', status: 'completed', time: '9:00 AM', duration: '2 hrs' },
];

const statusColors: Record<string, string> = {
  'on-site': 'bg-green-100 text-green-700',
  'en-route': 'bg-blue-100 text-blue-700',
  'idle': 'bg-amber-100 text-amber-700',
  'off-duty': 'bg-gray-100 text-gray-500',
};
const statusDot: Record<string, string> = {
  'on-site': 'bg-green-500',
  'en-route': 'bg-blue-500 animate-pulse',
  'idle': 'bg-amber-500',
  'off-duty': 'bg-gray-400',
};
const jobStatusColors: Record<string, string> = {
  'in-progress': 'bg-green-100 text-green-700',
  'en-route': 'bg-blue-100 text-blue-700',
  'scheduled': 'bg-amber-100 text-amber-700',
  'completed': 'bg-gray-100 text-gray-600',
};

// Mini Sri Lanka map (SVG placeholder)
function SriLankaMap({ technicians }: { technicians: Technician[] }) {
  return (
    <div className="relative bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl overflow-hidden h-80 border border-blue-100">
      {/* Grid lines */}
      <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 320">
        {[0, 80, 160, 240, 320].map(x => <line key={x} x1={x} y1="0" x2={x} y2="320" stroke="#60a5fa" strokeWidth="1" />)}
        {[0, 80, 160, 240, 320].map(y => <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#60a5fa" strokeWidth="1" />)}
      </svg>
      {/* Sri Lanka outline placeholder */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 320">
        {/* Simplified Sri Lanka island shape */}
        <ellipse cx="200" cy="160" rx="70" ry="120" fill="rgba(20,180,80,0.08)" stroke="rgba(20,180,80,0.3)" strokeWidth="2" />
        {/* City dots */}
        <circle cx="175" cy="220" r="4" fill="#60a5fa" opacity="0.6" />
        <text x="182" y="224" fontSize="9" fill="#3b82f6" opacity="0.7">Colombo</text>
        <circle cx="210" cy="120" r="4" fill="#60a5fa" opacity="0.6" />
        <text x="217" y="124" fontSize="9" fill="#3b82f6" opacity="0.7">Kandy</text>
        <circle cx="160" cy="190" r="4" fill="#60a5fa" opacity="0.6" />
        <text x="167" y="194" fontSize="9" fill="#3b82f6" opacity="0.7">Negombo</text>
        {/* Technician markers */}
        {technicians.filter(t => t.status !== 'off-duty').map((tech, i) => {
          const positions = [{ x: 175, y: 215 }, { x: 162, y: 185 }, { x: 212, y: 118 }];
          const pos = positions[i] || { x: 200, y: 160 };
          const colors: Record<string, string> = { 'on-site': '#22c55e', 'en-route': '#3b82f6', 'idle': '#f59e0b' };
          return (
            <g key={tech.id}>
              <circle cx={pos.x} cy={pos.y} r="10" fill={colors[tech.status] || '#6b7280'} opacity="0.2" />
              <circle cx={pos.x} cy={pos.y} r="6" fill={colors[tech.status] || '#6b7280'} />
              <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">
                {tech.avatar}
              </text>
            </g>
          );
        })}
      </svg>
      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex items-center gap-3">
        {[['on-site', 'On-site', '#22c55e'], ['en-route', 'En-route', '#3b82f6'], ['idle', 'Idle', '#f59e0b']].map(([key, label, color]) => (
          <div key={key} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color as string }} />
            <span className="text-[10px] text-gray-600">{label}</span>
          </div>
        ))}
      </div>
      <div className="absolute top-3 right-3 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-lg">
        <span className="text-[10px] font-semibold text-gray-600">LIVE GPS</span>
      </div>
    </div>
  );
}

export default function GPSTrackingPage() {
  const [technicians, setTechnicians] = useState<Technician[]>(technicianData);
  const [selectedTech, setSelectedTech] = useState<Technician | null>(technicians[0]);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Simulate live updates
  useEffect(() => {
    const interval = setInterval(() => {
      setTechnicians(prev => prev.map(t => ({
        ...t,
        batteryLevel: Math.max(10, t.batteryLevel - (t.status === 'off-duty' ? 0 : 1)),
        lat: t.lat + (Math.random() - 0.5) * 0.001,
        lng: t.lng + (Math.random() - 0.5) * 0.001,
      })));
      setLastUpdated(new Date());
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const techJobs = (techId: string) => jobData.filter(j => j.techId === techId);

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center shadow-lg shadow-green-200">
            <Navigation className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Live GPS Tracking</h1>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              <p className="text-xs text-gray-500">Last updated: {lastUpdated.toLocaleTimeString()}</p>
            </div>
          </div>
        </div>
        <button onClick={() => setLastUpdated(new Date())} className="flex items-center gap-2 text-xs text-gray-600 bg-white border border-gray-200 px-3 py-2 rounded-xl hover:bg-gray-50 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2">
          <SriLankaMap technicians={technicians} />
          
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3 mt-4">
            {[
              { label: 'On-site', value: technicians.filter(t => t.status === 'on-site').length, color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'En-route', value: technicians.filter(t => t.status === 'en-route').length, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Jobs Today', value: jobData.filter(j => j.status !== 'scheduled').length, color: 'text-violet-600', bg: 'bg-violet-50' },
              { label: 'Completed', value: jobData.filter(j => j.status === 'completed').length, color: 'text-gray-600', bg: 'bg-gray-50' },
            ].map((stat, i) => (
              <div key={i} className={`${stat.bg} rounded-xl p-3 text-center`}>
                <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Technician List */}
        <div className="space-y-3">
          {technicians.map(tech => (
            <motion.div
              key={tech.id}
              whileHover={{ scale: 1.01 }}
              onClick={() => setSelectedTech(tech)}
              className={`bg-white rounded-2xl border cursor-pointer transition-all p-4 ${selectedTech?.id === tech.id ? 'border-green-400 shadow-sm shadow-green-100' : 'border-gray-200 hover:shadow-sm'}`}
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-xs font-bold text-white">
                    {tech.avatar}
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${statusDot[tech.status]}`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{tech.name}</p>
                  <p className="text-xs text-gray-500 truncate">{tech.currentJob}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${statusColors[tech.status]}`}>
                  {tech.status}
                </span>
              </div>
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <MapPin className="w-3 h-3" /> {tech.location}
                </div>
                {tech.eta && tech.eta !== '—' && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" /> ETA: {tech.eta}
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <CheckCircle className="w-3 h-3" /> {tech.completedToday} jobs completed today
                </div>
                {/* Battery */}
                <div className="flex items-center gap-2 mt-2">
                  <Zap className="w-3 h-3 text-gray-400" />
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${tech.batteryLevel > 50 ? 'bg-green-500' : tech.batteryLevel > 20 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${tech.batteryLevel}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-400">{tech.batteryLevel}%</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Selected Tech Jobs */}
      {selectedTech && (
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">{selectedTech.name}'s Jobs Today</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {techJobs(selectedTech.id).map(job => (
              <div key={job.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-400">{job.id}</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${jobStatusColors[job.status]}`}>{job.status}</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{job.customer}</p>
                <p className="text-xs text-gray-600 mt-0.5">{job.service}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{job.time}</span>
                  <span className="text-xs text-gray-400">{job.duration}</span>
                </div>
                <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                  <MapPin className="w-3 h-3" /> {job.address}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
